import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Archive, Eye, Loader2 } from "lucide-react";

const SHOW = 12;
const WORDS = { parts: "Parts", containers: "Totes, crates & containers", trucks: "Trucks" };
const HELD = { parts: ["part", "parts"], containers: ["tote or crate", "totes or crates"], trucks: ["truck", "trucks"],
  people: ["person with it as their site", "people with it as their site"], transfers: ["transfer", "transfers"] };
const said = (held) => held.map(([k, v]) => `${v} ${(HELD[k] || [k, k])[v === 1 ? 0 : 1]}`).join(", ");

// WHAT IS STILL ON IT — listed by code, so he can go and move each one before archiving.
const OnIt = ({ onIt, onOpen }) => (
  <div data-testid="archive-tree-on-it" className="space-y-1.5 max-h-48 overflow-y-auto">
    {Object.entries(onIt).filter(([, v]) => v.length).map(([k, v]) => (
      <div key={k}>
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{WORDS[k]} ({v.length})</div>
        <div className="flex flex-wrap gap-1.5 mt-0.5">
          {v.slice(0, SHOW).map((x, i) => (
            <button type="button" key={`${x.code}-${i}`} data-testid={`archive-tree-item-${x.code}`} disabled={!x.on_id || !onOpen}
              onClick={() => onOpen?.(x.on_id)} title={x.on ? `Open ${x.on} to move it` : undefined}
              className="font-mono text-[11px] rounded-md bg-white border border-red-200 px-1.5 py-0.5 hover:border-red-500 transition-colors">
              {x.code || "no code"}{x.on ? ` @ ${x.on}` : ""}
            </button>
          ))}
          {v.length > SHOW && <span className="text-[11px] text-slate-500">and {v.length - SHOW} more</span>}
        </div>
      </div>
    ))}
  </div>
);

// TAKE OUT A PLACE NOBODY USES — it and everything under it, in one go. Counted first; refused while anything
// is on it; Restore (here on the toast, or any time from Archived) puts it all back. Archive, never delete.
export const ArchiveTreeDialog = ({ node, onClose, onDone, onSeeContents }) => {
  const [look, setLook] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!node) return;
    setLook(null);
    api.post(`/locations/${node.id}/archive-tree`, { confirm: false })
      .then((r) => setLook(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [node]);

  const undo = async (batchId) => {
    try {
      const { data } = await api.post(`/locations/archive-batches/${batchId}/undo`);
      toast.success(`${data.code}: ${data.restored} place(s) put back`);
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/locations/${node.id}/archive-tree`, { confirm: true });
      toast.success(`${data.code}: ${data.archived} place(s) archived`, {
        duration: 20000, action: { label: "Undo", onClick: () => undo(data.batch_id) } });
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const held = look ? Object.entries(look.holds).filter(([, v]) => v) : [];
  // "See what's on it" opens the place the first thing actually sits on — not the (maybe empty) top place
  const firstHolder = look ? Object.values(look.on_it || {}).flat().find((x) => x.on_id)?.on_id : null;
  return (
    <Dialog open={!!node} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="archive-tree-dialog" className="rounded-xl border-slate-200 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Archive {node?.name}{look?.places > 1 ? " and everything under it" : ""}?</DialogTitle>
          <DialogDescription>Nothing is deleted. It leaves every list and picker, its history stays, its code is never given to another place, and Restore brings it back.</DialogDescription>
        </DialogHeader>
        {!look ? <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Counting…</div> : (
          <div data-testid="archive-tree-summary" className="space-y-2 text-sm">
            <p><b className="font-mono">{look.code}</b> — <b data-testid="archive-tree-places">{look.places}</b> place(s) in all.
              {" "}{look.printed ? <><b data-testid="archive-tree-printed">{look.printed}</b> had a label printed — scanned later, those stickers will say the place was archived.</> : "No label print is on record for any of them."}</p>
            {held.length ? (
              <div data-testid="archive-tree-refused" className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2 space-y-2">
                <p>Can't archive yet — something is still on it: {said(held)}. Move them first — tap one to open the place it sits on.</p>
                <OnIt onIt={look.on_it} onOpen={onSeeContents} />
              </div>
            ) : <p className="text-slate-600">Nothing is on any of them — no part, tote, crate or truck.</p>}
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" data-testid="archive-tree-cancel" className="rounded-lg h-11 border-slate-200" onClick={onClose}>Cancel</Button>
          {firstHolder && onSeeContents && (
            <Button variant="outline" data-testid="archive-tree-see-contents" className="rounded-lg h-11 border-slate-200" onClick={() => onSeeContents(firstHolder)}>
              <Eye className="w-4 h-4 mr-1.5" />See what's on it
            </Button>
          )}
          <Button data-testid="archive-tree-confirm" disabled={busy || !look?.can_archive} onClick={go}
            className="rounded-lg h-11 bg-[#B01E28] hover:bg-[#8f1820] text-white font-bold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Archive className="w-4 h-4 mr-1.5" />Archive {look?.places || ""}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
